import { useEffect, useMemo, useState } from "react";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api, { inr } from "@/lib/api";
import { bust, thumb } from "@/lib/img";
import { useApp } from "@/context/AppContext";

const cleanSteps = (arr) => [...new Set((Array.isArray(arr) ? arr : []).map(Number).filter((n) => Number.isFinite(n) && n > 0))].sort((a, b) => a - b);

function DimPicker({ label, value, setter, testid, lo, hi, steps }) {
  const stepped = steps.length > 0;
  const idx = stepped ? Math.max(0, steps.indexOf(value)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-[#292724]">{label}</span>
        <span data-testid={`${testid}-value`} className="text-sm text-[#b8912a] font-mono">{value} ft</span>
      </div>
      <input type="range" data-testid={`${testid}-slider`} min={stepped ? 0 : lo} max={stepped ? Math.max(0, steps.length - 1) : hi} step="1"
        value={stepped ? idx : value} onChange={(e) => { const n = Number(e.target.value); setter(stepped ? steps[n] : n); }} className="w-full accent-[#d4af37]" />
      <div className="flex justify-between text-[10px] text-[#8a837a] mt-0.5"><span>{stepped ? steps[0] : lo} ft</span><span>{stepped ? steps[steps.length - 1] : hi} ft</span></div>
      {stepped && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {steps.map((s) => (
            <button key={s} type="button" data-testid={`${testid}-step-${s}`} onClick={() => setter(s)} aria-pressed={s === value}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${s === value ? "bg-[#d4af37] text-white border-[#d4af37]" : "bg-white text-[#292724] border-[#ebe6e0] hover:border-[#d4af37]"}`}>{s} ft</button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function QuickAddModal() {
  const { quickAdd, closeQuickAdd, addToSelection } = useApp();
  const [p, setP] = useState(null);
  const [variantId, setVariantId] = useState(null);
  const [qty, setQty] = useState(1);
  const [widthFt, setWidthFt] = useState(10);
  const [lengthFt, setLengthFt] = useState(10);

  useEffect(() => {
    if (!quickAdd) { setP(null); return; }
    document.body.style.overflow = "hidden";
    api.get(`/products/${quickAdd.productId}`).then((r) => {
      const d = r.data;
      setP(d);
      const vars = Array.isArray(d.variations) ? d.variations : [];
      setVariantId(vars.length ? (vars.find((v) => v.id === d.default_variation_id) || vars[0]).id : null);
      const wS = cleanSteps(d.size_width_steps), lS = cleanSteps(d.size_length_steps);
      setWidthFt(wS.length ? wS[0] : Math.max(d.size_width_min_ft ?? d.size_min_ft ?? 5, Math.min(d.size_width_max_ft ?? d.size_max_ft ?? 40, 10)));
      setLengthFt(lS.length ? lS[0] : Math.max(d.size_length_min_ft ?? d.size_min_ft ?? 5, Math.min(d.size_length_max_ft ?? d.size_max_ft ?? 40, 10)));
      const opts = Array.isArray(d.quantity_options) ? d.quantity_options : [];
      setQty(opts.length ? opts[0] : 1);
    }).catch(() => { toast.error("Could not load product"); closeQuickAdd(); });
    return () => { document.body.style.overflow = ""; };
  }, [quickAdd, closeQuickAdd]);

  const variations = useMemo(() => (p && Array.isArray(p.variations) ? p.variations : []), [p]);
  const variant = variations.find((v) => v.id === variantId) || null;
  if (!quickAdd) return null;

  const sizeOn = !!p?.size_customizable;
  const widthEnabled = sizeOn && p.size_width_enabled !== false;
  const lengthEnabled = sizeOn && p.size_length_enabled !== false;
  const wMin = p?.size_width_min_ft ?? p?.size_min_ft ?? 5, wMax = p?.size_width_max_ft ?? p?.size_max_ft ?? 40;
  const lMin = p?.size_length_min_ft ?? p?.size_min_ft ?? 5, lMax = p?.size_length_max_ft ?? p?.size_max_ft ?? 40;
  const rate = Number(p?.price_per_sqft) || 0;
  const sqft = Math.max(0, (widthEnabled ? Number(widthFt) : 1) * (lengthEnabled ? Number(lengthFt) : 1));
  const sizePrice = sizeOn && rate > 0 && (widthEnabled || lengthEnabled) ? Math.round(sqft * rate) : null;
  const basePrice = variant ? Number(variant.price) || 0 : (p?.price || 0);
  const unitPrice = sizePrice != null ? sizePrice : basePrice;
  const quantityOptions = Array.isArray(p?.quantity_options) ? p.quantity_options : [];
  const total = Math.round(unitPrice * Math.max(1, Number(qty) || 1));
  const displayName = variant ? `${p.name} · ${variant.name}` : p?.name;
  const image = (variant?.images?.[0]) || p?.primary_image;

  const confirm = () => {
    const extras = {
      price: sizePrice != null || variant ? unitPrice : undefined,
      variation: variant ? { id: variant.id, name: variant.name } : null,
      custom_size: sizeOn ? { width_ft: widthEnabled ? Number(widthFt) : null, length_ft: lengthEnabled ? Number(lengthFt) : null, sqft: Math.round(sqft) } : null,
    };
    const prod = variant ? { ...p, price: unitPrice, primary_image: image } : p;
    addToSelection(prod, qty, "", extras);
    toast.success("Added to My Selection", { description: displayName });
    quickAdd.onAdded && quickAdd.onAdded(p);
    closeQuickAdd();
  };

  return (
    <div data-testid="quick-add-modal" className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-[#292724]/70 backdrop-blur-md animate-in fade-in duration-200" onClick={closeQuickAdd}>
      <div className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-[#fcfaf8] shadow-2xl border border-[#d4af37]/25" onClick={(e) => e.stopPropagation()}>
        <button data-testid="quick-add-close" onClick={closeQuickAdd} className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/95 hover:bg-white flex items-center justify-center shadow" aria-label="Close"><X className="w-4 h-4" /></button>
        {!p ? (
          <div className="p-10 text-center text-sm text-[#8a837a]">Loading…</div>
        ) : (
          <>
            <div className="flex gap-4 p-5 border-b border-[#ebe6e0]">
              <img loading="lazy" src={thumb(bust(image, p.updated_at), 300)} alt={p.name} className="w-20 h-20 rounded-xl object-cover object-center bg-[#f4efeb] flex-shrink-0" />
              <div className="min-w-0 pr-8">
                <div className="eyebrow text-[10px] capitalize">{String(p.category || p.sub_category || "").replace(/_/g, " ")}</div>
                <div data-testid="quick-add-name" className="font-serif text-lg text-[#292724] leading-tight">{p.name}</div>
                <div className="text-sm text-[#d4af37] font-medium mt-1">{inr(unitPrice)} <span className="text-[#8a837a] text-xs">{sizePrice != null ? "for this size" : p.unit || "per event"}</span></div>
              </div>
            </div>

            <div className="p-5 space-y-5">
              {variations.length > 0 && (
                <div>
                  <div className="eyebrow text-[10px] mb-2">Choose a variation</div>
                  <div className="flex flex-wrap gap-2">
                    {variations.map((v) => (
                      <button key={v.id} type="button" data-testid={`quick-add-variation-${v.id}`} onClick={() => setVariantId(v.id)} aria-pressed={v.id === variantId}
                        className={`px-3.5 py-1.5 rounded-full border text-sm font-medium transition-all ${v.id === variantId ? "bg-[#292724] text-white border-[#292724]" : "bg-white text-[#292724] border-[#ebe6e0] hover:border-[#d4af37]"}`}>
                        {v.name}<span className={`ml-2 text-xs ${v.id === variantId ? "text-[#d4af37]" : "text-[#8a837a]"}`}>{inr(Number(v.price) || 0)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {sizeOn && (widthEnabled || lengthEnabled) && (
                <div data-testid="quick-add-size" className="rounded-2xl border border-[#ebe6e0] bg-white p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="eyebrow text-[10px]">Customize the size</div>
                    <div className="text-sm font-serif text-[#d4af37]">{widthEnabled && lengthEnabled ? `${Math.round(sqft)} sq. ft.` : `${widthEnabled ? widthFt : lengthFt} ft`}</div>
                  </div>
                  {widthEnabled && <DimPicker label="Width" value={widthFt} setter={setWidthFt} testid="quick-size-width" lo={wMin} hi={wMax} steps={cleanSteps(p.size_width_steps)} />}
                  {lengthEnabled && <DimPicker label="Length" value={lengthFt} setter={setLengthFt} testid="quick-size-length" lo={lMin} hi={lMax} steps={cleanSteps(p.size_length_steps)} />}
                </div>
              )}

              <div>
                <div className="eyebrow text-[10px] mb-2">Quantity</div>
                {quantityOptions.length > 0 ? (
                  <div data-testid="quick-add-qty-presets" className="flex flex-wrap gap-2">
                    {quantityOptions.map((opt) => (
                      <button key={opt} type="button" data-testid={`quick-add-qty-${opt}`} onClick={() => setQty(opt)} aria-pressed={qty === opt}
                        className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${qty === opt ? "bg-[#d4af37] text-white border-[#d4af37]" : "bg-white text-[#292724] border-[#ebe6e0] hover:border-[#d4af37]"}`}>{opt}</button>
                    ))}
                  </div>
                ) : (
                  <div className="inline-flex items-center border border-[#ebe6e0] rounded-full bg-white">
                    <button type="button" data-testid="quick-add-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="p-2"><Minus className="w-4 h-4" /></button>
                    <span data-testid="quick-add-qty-value" className="px-3 text-sm">{qty}</span>
                    <button type="button" data-testid="quick-add-qty-plus" onClick={() => setQty(qty + 1)} className="p-2"><Plus className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
            </div>

            <div className="p-5 pt-0 flex items-center justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#8a837a]">Total</div>
                <div data-testid="quick-add-total" className="font-serif text-2xl text-[#292724]">{inr(total)}</div>
              </div>
              <button type="button" data-testid="quick-add-confirm" onClick={confirm} className="btn-gold"><ShoppingBag className="w-4 h-4" /> Add to Cart</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
